"use client";

import { Loader2 } from "lucide-react";
import { useCallback, useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import logger from "@/lib/logger";
import type { PendingChoiceView } from "@/lib/systems/pending-choices/read-view";
import { callMcpTool } from "@/src/api/mcp";
import { useGameState } from "./game-state-context";

type PendingChoiceCardProps = {
  choice: PendingChoiceView;
  campaignId: string;
  onResolved?: () => void;
};

export function PendingChoiceCard({
  choice,
  campaignId,
  onResolved,
}: PendingChoiceCardProps) {
  const inputId = useId();
  const [x, setX] = useState(choice.constraints.origin?.x ?? 0);
  const [y, setY] = useState(choice.constraints.origin?.y ?? 0);
  const [z, setZ] = useState(choice.constraints.origin?.z ?? 0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { invalidate } = useGameState();

  const refreshDomains = useCallback(() => {
    invalidate(["pending-choices"]);
  }, [invalidate]);

  const handleSkip = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await callMcpTool(
        "resolve_pending_choice",
        { choiceId: choice.entityId, decision: "skip" },
        campaignId
      );
      refreshDomains();
      onResolved?.();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to skip choice";
      logger.error(
        { err, choiceId: choice.entityId },
        "Failed to skip pending choice"
      );
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [campaignId, choice.entityId, onResolved, refreshDomains]);

  const handleResolve = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await callMcpTool(
        "resolve_pending_choice",
        {
          choiceId: choice.entityId,
          decision: "resolve",
          destination: { x, y, z },
        },
        campaignId
      );
      refreshDomains();
      onResolved?.();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to resolve choice";
      logger.error(
        { err, choiceId: choice.entityId },
        "Failed to resolve pending choice"
      );
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [campaignId, choice.entityId, onResolved, refreshDomains, x, y, z]);

  if (choice.kind !== "point_destination") {
    return null;
  }

  return (
    <Card className="my-2 overflow-hidden rounded-lg border-blue-500/50 bg-card p-3">
      <div className="min-w-0 space-y-3">
        <p className="break-words text-card-foreground text-sm leading-5">
          {choice.prompt}
        </p>
        <div className="grid grid-cols-3 gap-2">
          <label
            className="min-w-0 space-y-1 text-muted-foreground text-xs"
            htmlFor={`${inputId}-x`}
          >
            <span>X</span>
            <Input
              className="h-9 tabular-nums"
              disabled={isSubmitting}
              id={`${inputId}-x`}
              onChange={(event) => setX(Number(event.target.value))}
              type="number"
              value={x}
            />
          </label>
          <label
            className="min-w-0 space-y-1 text-muted-foreground text-xs"
            htmlFor={`${inputId}-y`}
          >
            <span>Y</span>
            <Input
              className="h-9 tabular-nums"
              disabled={isSubmitting}
              id={`${inputId}-y`}
              onChange={(event) => setY(Number(event.target.value))}
              type="number"
              value={y}
            />
          </label>
          <label
            className="min-w-0 space-y-1 text-muted-foreground text-xs"
            htmlFor={`${inputId}-z`}
          >
            <span>Z</span>
            <Input
              className="h-9 tabular-nums"
              disabled={isSubmitting}
              id={`${inputId}-z`}
              onChange={(event) => setZ(Number(event.target.value))}
              type="number"
              value={z}
            />
          </label>
        </div>
        {error ? (
          <div className="break-words text-destructive text-xs">{error}</div>
        ) : null}
        <div className="flex min-w-0 gap-2">
          <Button
            className="min-w-0 flex-1"
            disabled={isSubmitting}
            onClick={handleSkip}
            size="sm"
            type="button"
            variant="outline"
          >
            Skip
          </Button>
          <Button
            className="min-w-0 flex-1"
            disabled={isSubmitting}
            onClick={handleResolve}
            size="sm"
            type="button"
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Resolve
          </Button>
        </div>
      </div>
    </Card>
  );
}
